// 競合比較(Instagram / X の competitor_analysis_controller)のサマリー集計(#1624)。
//
// 平均エンゲージメント率と3枚目のカード(Instagram=平均投稿数、X=平均エンゲージメント数)は、
// サーバーがアカウントごとに集計した stats を使う(自社 KPI と同じ「期間内の投稿ごとの値の平均」)。
// 日別チャート系列は投稿の無い日が 0 で埋まっているため、そこから平均し直すと値が薄まる。
//   Instagram: stats = { avg_posts, avg_engagement_rate }
//   X:         stats = { avg_engagement, avg_engagement_rate }
// 対象投稿が無いアカウントの値は null で、表示は「－」、競合平均の分母から除外する。

export const EMPTY_VALUE = "－"

const toNullableNumber = (value) => (typeof value === "number" && Number.isFinite(value) ? value : null)

// チャート系列([{ x, y }] または数値の配列)から null を除いた値の配列を返す
export function extractSeriesValues(series) {
  return (series || [])
    .map((point) => (point !== null && typeof point === "object" ? point.y : point))
    .filter((value) => value != null)
}

// null を除いた平均。値が1件も無ければ null
export function averageIgnoringNull(values) {
  const present = values.map(toNullableNumber).filter((value) => value != null)
  if (present.length === 0) return null
  return present.reduce((sum, value) => sum + value, 0) / present.length
}

// stats が無い(旧データ構造)場合だけ、従来どおりチャート系列の単純平均で代用する
function statOrSeriesAverage(stats, key, series) {
  if (stats && Object.hasOwn(stats, key)) return toNullableNumber(stats[key])
  return averageIgnoringNull(extractSeriesValues(series))
}

// 1アカウント分のサマリー値。followers / maxLikes はチャート系列から(最新値・期間最大)
export function computeAccountStats(account) {
  const data = account?.data || {}
  const stats = account?.stats
  const followerValues = extractSeriesValues(data.follower)
  const likesValues = extractSeriesValues(data.impression)
  const thirdKey = stats && Object.hasOwn(stats, "avg_posts") ? "avg_posts" : "avg_engagement"

  return {
    followers: followerValues.length > 0 ? followerValues[followerValues.length - 1] : 0,
    maxLikes: likesValues.length > 0 ? Math.max(...likesValues) : 0,
    avgPosts: statOrSeriesAverage(stats, thirdKey, data.avg_engagement),
    avgEngagement: statOrSeriesAverage(stats, "avg_engagement_rate", data.avg_engagement_rate)
  }
}

// 競合アカウント平均。各値は null(対象投稿なし)を分母から除いた単純平均で、全員 null なら null
export function computeCompetitorAverage(statsList) {
  return {
    followers: averageIgnoringNull(statsList.map((stats) => stats.followers)) ?? 0,
    maxLikes: averageIgnoringNull(statsList.map((stats) => stats.maxLikes)) ?? 0,
    avgPosts: averageIgnoringNull(statsList.map((stats) => stats.avgPosts)),
    avgEngagement: averageIgnoringNull(statsList.map((stats) => stats.avgEngagement))
  }
}

// 自社 - 競合平均。どちらかが null なら null(差分は表示しない)
export function difference(main, competitorAverage) {
  if (toNullableNumber(main) == null || toNullableNumber(competitorAverage) == null) return null
  return main - competitorAverage
}

// 小数表示。null は「－」
export function formatFixed(value, digits, suffix = "") {
  const number = toNullableNumber(value)
  return number == null ? EMPTY_VALUE : `${number.toFixed(digits)}${suffix}`
}
